import { useMemo, type ReactNode } from 'react';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { exportQueryOf, useListSearch } from '@eis/core';
import {
  ExportButton,
  InfiniteDataGrid,
  PageHeader,
  PageLayout,
  PropertyFilter,
  RequirePermission,
  cardSurfaceSx,
  useGridExportColumns,
  type ExportableGridColDef,
} from '@eis/patterns';
import type { ConfigApi } from '@eis/insuranceservice-sdk';
import { insurancePermissions as p } from '../../../permissions';
import { useFilterCatalog, useInfiniteList } from '../api/group-tarifs';

const SORT_KEYS: Readonly<Record<string, string>> = { name: 'name', slug: 'slug' };

/**
 * Every group tarif in the tenant, on the list standard (SDD-017 §9): property filter, search,
 * server order and one scroll, all in the URL.
 *
 * The linked tarif has its own column, because it is the one fact about a group tarif that can
 * never change: a group tarif references exactly one tarif (decision D-3).
 */
export function GroupTarifListPage(): ReactNode {
  const { t } = useTranslation('insurance');
  const navigate = useNavigate();
  const url = useListSearch();
  const catalog = useFilterCatalog('groupTarifs');
  const list = useInfiniteList('groupTarifs', url.params);

  // Each column is a field of `GroupTarif` (SDD-020 §8.1): the export writes what the grid shows.
  const columns = useMemo<ExportableGridColDef<ConfigApi.GroupTarif>[]>(
    () => [
      {
        field: 'name',
        headerName: t('groupTarifs.list.name'),
        flex: 1,
        minWidth: 240,
        exportPath: 'name',
      },
      { field: 'slug', headerName: t('groupTarifs.list.slug'), width: 180, exportPath: 'slug' },
      {
        field: 'tarifErn',
        headerName: t('groupTarifs.list.tarif'),
        exportPath: 'tarifErn',
        flex: 1,
        minWidth: 240,
        renderCell: ({ row }) => (
          <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace' }}>
            {row.tarifErn}
          </Typography>
        ),
      },
    ],
    [t],
  );
  const exportColumns = useGridExportColumns(columns);

  const header = (
    <PageHeader
      title={t('groupTarifs.title')}
      description={t('groupTarifs.subtitle')}
      actions={
        // The same chooser as Tarife: a Group Tarif cannot be created without naming the tarif it
        // links to, so there is one screen that knows about both rather than two that half do.
        <Stack direction="row" spacing={1}>
          <ExportButton
            service="insurance"
            permission={p.exports.create}
            rowCount={list.isLoading ? undefined : list.items.length}
            buildRequest={(format) => ({
              collection: 'group-tarifs',
              query: exportQueryOf(url.params),
              columns: exportColumns,
              format,
              label: t('groupTarifs.title'),
            })}
          />
          <RequirePermission anyOf={[p.groupTariffs.create]}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                void navigate({ to: '/insurance/tarifs/new' });
              }}
            >
              {t('authoring.create.action')}
            </Button>
          </RequirePermission>
        </Stack>
      }
    />
  );

  return (
    <PageLayout header={header}>
      <Paper variant="outlined" sx={(theme) => ({ p: 2.5, ...cardSurfaceSx(theme) })}>
        <Stack spacing={2}>
          <PropertyFilter
            catalog={catalog.data}
            value={{ filters: url.params.filters ?? '', search: url.params.search ?? '' }}
            onChange={(next) => {
              url.set({ filters: next.filters, search: next.search });
            }}
          />
          <InfiniteDataGrid
            rows={list.items}
            list={list}
            columns={columns}
            getRowId={(row) => row.slug ?? row.ern ?? ''}
            ariaLabel={t('groupTarifs.list.ariaLabel')}
            order={url.params.order}
            onOrderChange={(order) => {
              url.set({ order });
            }}
            sortKeys={SORT_KEYS}
            errorDescription={t('groupTarifs.error.list')}
            emptyDescription={t('groupTarifs.list.empty')}
            onRowClick={(row) => {
              const slug = row.slug ?? '';
              if (slug === '') return;
              void navigate({ to: '/insurance/group-tarifs/$slug', params: { slug } });
            }}
          />
        </Stack>
      </Paper>
    </PageLayout>
  );
}
